import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import Chatbot from '../../components/chatbot/Chatbot';
import { chatbotSessionState } from '../../models/chatbot/recoil-state';
import {
  useSuggestionsQuery,
  useChatbotResumeQuery,
} from '../../common/queries/use-chatbot-query';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import advocateSelectors from '../../models/advocate/selectors';
import {
  getChatThreadId,
  saveChatThreadId,
} from '../../lib/chat-session-storage';
import styles from './assistant-side-panel.module.scss';

export function ChatbotHome() {
  const [chatbotSession, setChatbotSession] =
    useRecoilState(chatbotSessionState);
  const { threadId, isThreadPersisted, shouldFocusInput, skipResume } =
    chatbotSession;

  const programId = usePatronSelector(programSelectors.getProgramId);
  const advocateId = usePatronSelector(advocateSelectors.getAdvocateId);

  const hasAttemptedResume = useRef(false);
  const [hasCheckedStoredThread, setHasCheckedStoredThread] = useState(false);
  const storedThreadIdRef = useRef<string | undefined>(undefined);

  // Only call the resume API when there's no threadId in Recoil or sessionStorage
  // and the user hasn't explicitly started a new conversation
  const shouldCallResume =
    hasCheckedStoredThread &&
    !threadId &&
    !storedThreadIdRef.current &&
    !hasAttemptedResume.current &&
    !skipResume;

  const { data: resumeThread } = useChatbotResumeQuery(String(programId), {
    enabled: !!programId && shouldCallResume,
  });

  // Restore from sessionStorage once IDs are available before attempting resume.
  useEffect(() => {
    if (hasCheckedStoredThread || !advocateId || !programId) {
      return;
    }

    const stored = getChatThreadId(String(advocateId), String(programId));
    storedThreadIdRef.current = stored ?? undefined;
    setHasCheckedStoredThread(true);

    if (!threadId && stored) {
      storedThreadIdRef.current = undefined;
      setChatbotSession((prev) => ({
        ...prev,
        threadId: stored,
        isThreadPersisted: true,
      }));
    }
  }, [
    advocateId,
    hasCheckedStoredThread,
    programId,
    threadId,
    setChatbotSession,
  ]);

  // When skipResume is set (user clicked "New conversation"), consume and reset it
  useEffect(() => {
    if (skipResume) {
      hasAttemptedResume.current = true;
      setChatbotSession((prev) => ({
        ...prev,
        skipResume: false,
      }));
    }
  }, [skipResume, setChatbotSession]);

  // Restore from resume API if sessionStorage had nothing
  useEffect(() => {
    if (skipResume || hasAttemptedResume.current) return;

    if (!threadId && !storedThreadIdRef.current && resumeThread) {
      hasAttemptedResume.current = true;
      setChatbotSession((prev) => ({
        ...prev,
        threadId: resumeThread.id,
        isThreadPersisted: true,
      }));
      if (advocateId && programId) {
        saveChatThreadId(
          String(advocateId),
          String(programId),
          resumeThread.id
        );
      }
    } else if (
      !threadId &&
      !storedThreadIdRef.current &&
      resumeThread === null
    ) {
      hasAttemptedResume.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    threadId,
    resumeThread,
    skipResume,
    advocateId,
    programId,
    setChatbotSession,
  ]);

  // Persist threadId to sessionStorage whenever it changes
  useEffect(() => {
    if (threadId && advocateId && programId) {
      saveChatThreadId(String(advocateId), String(programId), threadId);
    }
  }, [threadId, advocateId, programId]);

  // Fetch suggestions - always available for fresh conversations
  const { data: suggestionsData } = useSuggestionsQuery(programId, {
    enabled: !!programId,
  });

  const handleThreadChange = useCallback(
    (newThreadId: string, persisted = false) => {
      setChatbotSession((prev) => ({
        ...prev,
        threadId: newThreadId,
        isThreadPersisted: persisted,
      }));
    },
    [setChatbotSession]
  );

  const handleFocusHandled = useCallback(() => {
    setChatbotSession((prev) => ({
      ...prev,
      shouldFocusInput: false,
    }));
  }, [setChatbotSession]);

  return (
    <section className={styles.assistantLanding} aria-label="Chatbot Assistant">
      <Chatbot
        threadId={threadId}
        isThreadPersisted={isThreadPersisted}
        onThreadChange={handleThreadChange}
        suggestions={suggestionsData?.suggestions}
        shouldFocusInput={shouldFocusInput}
        onFocusHandled={handleFocusHandled}
      />
    </section>
  );
}

export default ChatbotHome;
